import {AxiosError} from "axios";
import {AgendaBirthday} from "@/core/interfaces/AgendaBirthday";
import {apiErrorMessage} from "@/core/http/apiErrorMessage";

/**
 * The aggregate label of a day's birthdays. Never a chip per patient: a
 * pharmacy of 5 000 patients celebrates a dozen a day, some four hundred a
 * month. The word is spelled out — the chip truncates rather than abbreviates.
 */
export const birthdayChipLabel = (count: number): string =>
    count > 1 ? `${count} anniversaires` : `${count} anniversaire`;

/**
 * Who celebrates, for a tooltip or a popover. Names stay out of the grid
 * itself — the chip carries the count, the hover carries the people.
 */
export const birthdayNames = (birthdays: AgendaBirthday[]): string => birthdays
    .map(birthday => `${birthday.first_name} ${birthday.last_name} (${birthday.age})`)
    .join(' · ');

/** Y-m-d in the workstation's own timezone, the key birthdays are grouped on. */
export const birthdayDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/**
 * Why a birthday message was refused, in the counter team's words. The batch
 * endpoint reports per patient, so a single send needs its reason spelled out
 * rather than a bare failure.
 */
const SEND_REFUSAL_LABELS: Record<string, string> = {
    not_celebrating: 'ce patient ne fête pas son anniversaire aujourd\'hui',
    already_served: 'ce patient a déjà reçu son message cette année',
    no_matching_gift: 'le cadeau choisi n\'est plus actif',
    no_valid_phone: 'aucun mobile ne permet de le joindre',
    no_credit: 'les crédits SMS sont épuisés',
    send_failed: 'le message a été refusé par l\'opérateur',
    auto_conflict: 'plusieurs cadeaux ciblent ce patient'
};

export const birthdaySendRefusalLabel = (reason: string | null): string => {
    const known = reason === null ? undefined : SEND_REFUSAL_LABELS[reason];

    return known ? `Message non envoyé : ${known}.` : 'Le message n\'a pas pu être envoyé';
};

/**
 * What the panel hands over for one patient: who to greet, the gift the
 * pharmacist picked for them, and the mobile they typed when the record holds
 * none a message can reach. The phone is certified before the send, so it
 * never travels with the batch itself.
 */
export interface BirthdaySendRecipient {
    customer_id: number;
    birthday_gift_id?: number;
    phone?: string;
}

/**
 * Why a mobile could not be saved. The 409 is the certification's own guard:
 * the number already belongs to another patient of the pharmacy, and silently
 * re-pointing it would send this patient's message to someone else.
 */
export const certificationErrorMessage = (error: unknown): string =>
    error instanceof AxiosError && error.response?.status === 409
        ? 'Ce numéro est déjà rattaché à un autre patient de la pharmacie'
        : apiErrorMessage(error, 'Le numéro n\'a pas pu être enregistré');
